import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileHubApi } from '../lupp.service';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import type {
  FileUploadRequest,
  FileUploadResponse,
  ConfirmFileUploadResponse,
  FileDownloadResponse,
  FileMetadata,
  DeleteFileResponse,
  SearchFilesRequest,
  SearchFilesResponse,
} from './dto.fileUpload';

/*
|--------------------------------------------------------------------------
| UPLOAD FILE
|--------------------------------------------------------------------------
| POST /filehub/upload
| Initiates a file upload and returns presigned PUT URL (valid for 15 minutes)
*/

export type UseUploadFileParams = {
  onSuccess?: (data: FileUploadResponse) => void;
  onError?: (error: Error) => void;
};

export const useUploadFile = (params?: UseUploadFileParams) => {
  return useMutation({
    mutationFn: async (data: FileUploadRequest) => {
      const response = await FileHubApi.post<FileUploadRequest, FileUploadResponse>(
        '/shared/filehub/upload',
        data,
        { extractData: (response) => response as unknown as FileUploadResponse }
      );
      return response;
    },
    onSuccess: params?.onSuccess,
    onError: params?.onError,
  });
};

/*
|--------------------------------------------------------------------------
| CONFIRM FILE UPLOAD
|--------------------------------------------------------------------------
| POST /shared/filehub/upload/{fileId}/confirm
| Confirms that file has been uploaded to S3 and changes status to AVAILABLE
| Returns 200 OK (empty response)
*/

export type UseConfirmFileUploadParams = {
  onSuccess?: (fileId: string) => void;
  onError?: (error: Error) => void;
};

export const useConfirmFileUpload = (params?: UseConfirmFileUploadParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      await FileHubApi.post<undefined, ConfirmFileUploadResponse>(
        `/shared/filehub/upload/${fileId}/confirm`,
        undefined
      );
      return fileId;
    },
    onSuccess: (fileId) => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.FILES] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.FILE_METADATA, fileId] });
      params?.onSuccess?.(fileId);
    },
    onError: params?.onError,
  });
};

/*
|--------------------------------------------------------------------------
| GET DOWNLOAD URL
|--------------------------------------------------------------------------
| GET /shared/filehub/files/{fileId}/download
| Returns presigned GET URL for downloading (valid for 60 minutes)
*/

export type UseGetDownloadUrlParams = {
  fileId: string;
  enabled?: boolean;
};

export const useGetDownloadUrl = ({ fileId, enabled = true }: UseGetDownloadUrlParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FILE_DOWNLOAD, fileId],
    queryFn: async () => {
      const response = await FileHubApi.get<FileDownloadResponse>(
        `/shared/filehub/files/${fileId}/download`
      );
      return response;
    },
    enabled: enabled && !!fileId,
    staleTime: 30 * 60 * 1000, // 30 minutes (half of 60 min TTL)
  });
};

/*
|--------------------------------------------------------------------------
| GET FILE METADATA
|--------------------------------------------------------------------------
| GET /shared/filehub/files/{fileId}
| Returns file metadata
*/

export type UseGetFileMetadataParams = {
  fileId: string;
  enabled?: boolean;
};

export const useGetFileMetadata = ({ fileId, enabled = true }: UseGetFileMetadataParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FILE_METADATA, fileId],
    queryFn: async () => {
      const response = await FileHubApi.get<FileMetadata>(`/shared/filehub/files/${fileId}`);
      return response;
    },
    enabled: enabled && !!fileId,
  });
};

/*
|--------------------------------------------------------------------------
| DELETE FILE
|--------------------------------------------------------------------------
| DELETE /shared/filehub/files/{fileId}?reason={reason}
| Soft deletes file (marks as DELETED, doesn't physically remove)
| Returns 204 No Content on success
*/

export type UseDeleteFileParams = {
  onSuccess?: (fileId: string) => void;
  onError?: (error: Error) => void;
};

export const useDeleteFile = (params?: UseDeleteFileParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, reason }: { fileId: string; reason?: string }) => {
      const url = reason
        ? `/shared/filehub/files/${fileId}?reason=${encodeURIComponent(reason)}`
        : `/shared/filehub/files/${fileId}`;
      await FileHubApi.delete<undefined, DeleteFileResponse>(url);
      return fileId;
    },
    onSuccess: (fileId) => {
      queryClient.invalidateQueries({
        queryKey: [LUPP_QUERY_KEYS.FILE_METADATA, fileId],
      });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.FILES] });
      params?.onSuccess?.(fileId);
    },
    onError: params?.onError,
  });
};

/*
|--------------------------------------------------------------------------
| SEARCH FILES
|--------------------------------------------------------------------------
| POST /shared/filehub/files/search
| Search/filter files by customerId, category, status, date range, mimeType
*/

export type UseSearchFilesParams = SearchFilesRequest & {
  enabled?: boolean;
};

export const useSearchFiles = ({ enabled = true, ...searchParams }: UseSearchFilesParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FILES, searchParams],
    queryFn: async () => {
      const response = await FileHubApi.post<SearchFilesRequest, SearchFilesResponse>(
        '/shared/filehub/files/search',
        searchParams
      );
      return response;
    },
    enabled,
  });
};
